/* ============================================================================
   VendingScoreCard.tsx
   VendingScoreCard.com — ReactSet Component
   Version: v1.0.0
   Chat link: https://chatgpt.com/c/ (paste this chat’s link here)
   ----------------------------------------------------------------------------
   Purpose:
   Help businesses evaluate their current vending / micro-market services provider
   (machines + stocking + maintenance + support) using a weighted scorecard.
   ============================================================================ */

import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  ListGroup,
  Modal,
  ProgressBar,
  Row,
  Stack,
} from "react-bootstrap";
import {
  Clipboard,
  ClipboardCheck,
  Download,
  InfoCircle,
  ArrowClockwise,
  StarFill,
  ExclamationTriangleFill,
  CheckCircleFill,
} from "react-bootstrap-icons";

/* =============================================================================
   Types
============================================================================= */
type ServiceType = "Vending" | "Micro-Market" | "Both";
type RatingValue = 1 | 2 | 3 | 4 | 5;

type ScoreField =
  | "uptime"
  | "serviceResponse"
  | "stockingFrequency"
  | "productFit"
  | "priceFairness"
  | "priceTransparency"
  | "paymentExperience"
  | "equipmentQuality"
  | "cleanliness"
  | "reporting"
  | "communication"
  | "accountability"
  | "contractFlexibility"
  | "issueResolution"
  | "innovation"
  | "employeeSatisfaction";

type WeightMap = Record<ScoreField, number>;

type ScoreCardModel = {
  companyName: string;
  locationLabel: string;
  scope: ServiceType;
  providerName: string;
  contractEndDate: string; // ISO string or blank
  locationsCount: number;
  machinesCount: number;
  microMarketsCount: number;

  // ratings (1..5)
  ratings: Record<ScoreField, RatingValue>;

  // operational inputs
  avgWeeklyFootTraffic: number;
  stockoutsPerWeek: number;
  serviceTicketsPerMonth: number;

  // pricing inputs
  commissionOrRevenueShareKnown: boolean;
  pricingMarkupFeelsHigh: boolean;

  // free-text notes
  biggestWins: string;
  biggestPainPoints: string;
  wishList: string;

  // red flags toggles
  redFlags: {
    recurringExpiredProduct: boolean;
    repeatedPaymentFailures: boolean;
    chronicStockouts: boolean;
    slowService: boolean;
    poorCleanliness: boolean;
    priceGougingSuspected: boolean;
    contractAutoRenewTraps: boolean;
  };
};

type Category = {
  id: string;
  title: string;
  description: string;
  items: Array<{
    field: ScoreField;
    label: string;
    help: string;
  }>;
};

const DEFAULT_WEIGHTS: WeightMap = {
  uptime: 9,
  serviceResponse: 9,
  stockingFrequency: 8,
  productFit: 8,
  priceFairness: 8,
  priceTransparency: 7,
  paymentExperience: 7,
  equipmentQuality: 6,
  cleanliness: 6,
  reporting: 4,
  communication: 7,
  accountability: 6,
  contractFlexibility: 4,
  issueResolution: 8,
  innovation: 3,
  employeeSatisfaction: 10,
};

const CATEGORIES: Category[] = [
  {
    id: "operations",
    title: "Operations & Reliability",
    description: "Uptime, service response, stocking cadence, issue resolution.",
    items: [
      {
        field: "uptime",
        label: "Machine / Market Uptime",
        help: "How consistently equipment is working and available.",
      },
      {
        field: "serviceResponse",
        label: "Service Response Speed",
        help: "How quickly the provider responds and dispatches service.",
      },
      {
        field: "stockingFrequency",
        label: "Stocking / Replenishment",
        help: "How well inventory is maintained and refilled.",
      },
      {
        field: "issueResolution",
        label: "Issue Resolution Quality",
        help: "Problems are fixed correctly the first time, not repeated.",
      },
    ],
  },
  {
    id: "products",
    title: "Product Fit & Experience",
    description: "Product selection, employee satisfaction, payments, equipment quality.",
    items: [
      {
        field: "productFit",
        label: "Product Fit (Selection)",
        help: "Right mix for your team: healthy, indulgent, dietary needs, etc.",
      },
      {
        field: "employeeSatisfaction",
        label: "Employee Satisfaction",
        help: "Overall happiness and usage of vending / micro-market options.",
      },
      {
        field: "paymentExperience",
        label: "Payment Experience",
        help: "Card/tap/mobile reliability, refunds, and overall checkout flow.",
      },
      {
        field: "equipmentQuality",
        label: "Equipment Quality & Modernity",
        help: "Modern machines / kiosks, refrigeration, screens, accessibility.",
      },
    ],
  },
  {
    id: "pricing",
    title: "Pricing & Transparency",
    description: "Fair pricing, markup clarity, and trust in the provider’s economics.",
    items: [
      {
        field: "priceFairness",
        label: "Price Fairness",
        help: "Prices feel competitive for your area and employee expectations.",
      },
      {
        field: "priceTransparency",
        label: "Price Transparency",
        help: "Clear pricing rules, predictable changes, fair refunds/credits.",
      },
    ],
  },
  {
    id: "management",
    title: "Provider Relationship",
    description: "Communication, accountability, reporting, contract flexibility, innovation.",
    items: [
      {
        field: "communication",
        label: "Communication",
        help: "Proactive updates, clear expectations, and easy contact.",
      },
      {
        field: "accountability",
        label: "Accountability",
        help: "Owns outcomes (stockouts, downtime) and follows through.",
      },
      {
        field: "reporting",
        label: "Reporting & Insights",
        help: "Sales, best-sellers, stockout trends, and service metrics.",
      },
      {
        field: "contractFlexibility",
        label: "Contract Flexibility",
        help: "Reasonable terms, no traps, fair exit/renewal process.",
      },
      {
        field: "innovation",
        label: "Innovation",
        help: "New products, tech upgrades, fresh ideas, seasonal rotations.",
      },
    ],
  },
  {
    id: "standards",
    title: "Cleanliness & Standards",
    description: "Hygiene, merchandising, and overall appearance.",
    items: [
      {
        field: "cleanliness",
        label: "Cleanliness & Merchandising",
        help: "Machines and market area are clean, labeled, and presentable.",
      },
    ],
  },
];

const clampInt = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));
const asRating = (n: number): RatingValue => clampInt(Math.round(n), 1, 5) as RatingValue;

function ratingLabel(v: RatingValue) {
  if (v >= 5) return "Excellent";
  if (v === 4) return "Good";
  if (v === 3) return "OK";
  if (v === 2) return "Needs Work";
  return "Poor";
}

function scoreToGrade(pct: number) {
  if (pct >= 90) return { grade: "A", intent: "success" as const };
  if (pct >= 80) return { grade: "B", intent: "success" as const };
  if (pct >= 70) return { grade: "C", intent: "warning" as const };
  if (pct >= 60) return { grade: "D", intent: "warning" as const };
  return { grade: "F", intent: "danger" as const };
}

function buildDefaultModel(): ScoreCardModel {
  const defaultRatings = Object.keys(DEFAULT_WEIGHTS).reduce((acc, k) => {
    acc[k as ScoreField] = 3 as RatingValue;
    return acc;
  }, {} as Record<ScoreField, RatingValue>);

  return {
    companyName: "",
    locationLabel: "",
    scope: "Both",
    providerName: "",
    contractEndDate: "",
    locationsCount: 1,
    machinesCount: 1,
    microMarketsCount: 0,

    ratings: defaultRatings,

    avgWeeklyFootTraffic: 250,
    stockoutsPerWeek: 3,
    serviceTicketsPerMonth: 2,

    commissionOrRevenueShareKnown: false,
    pricingMarkupFeelsHigh: false,

    biggestWins: "",
    biggestPainPoints: "",
    wishList: "",

    redFlags: {
      recurringExpiredProduct: false,
      repeatedPaymentFailures: false,
      chronicStockouts: false,
      slowService: false,
      poorCleanliness: false,
      priceGougingSuspected: false,
      contractAutoRenewTraps: false,
    },
  };
}

function computeWeightedScore(model: ScoreCardModel, weights: WeightMap) {
  const entries = Object.entries(weights) as Array<[ScoreField, number]>;
  const max = entries.reduce((sum, [, w]) => sum + w * 5, 0);
  const raw = entries.reduce((sum, [field, w]) => sum + w * model.ratings[field], 0);

  // Mild penalty for red flags (keeps score realistic)
  const redFlagCount = Object.values(model.redFlags).filter(Boolean).length;
  const redFlagPenalty = Math.min(12, redFlagCount * 2); // cap at 12%

  const pctNoPenalty = (raw / max) * 100;
  const pct = Math.max(0, pctNoPenalty - redFlagPenalty);

  return {
    raw,
    max,
    pct,
    pctNoPenalty,
    redFlagCount,
    redFlagPenalty,
  };
}

function buildKeyFindings(model: ScoreCardModel) {
  const top: Array<{ label: string; field: ScoreField; value: RatingValue }> = [];
  const bottom: Array<{ label: string; field: ScoreField; value: RatingValue }> = [];

  const allItems = CATEGORIES.flatMap((c) => c.items).map((i) => ({
    label: i.label,
    field: i.field,
    value: model.ratings[i.field],
  }));

  const sorted = [...allItems].sort((a, b) => b.value - a.value);
  top.push(...sorted.slice(0, 4));
  bottom.push(...sorted.slice(-4).reverse());

  const redFlagsOn = Object.entries(model.redFlags)
    .filter(([, v]) => v)
    .map(([k]) => k);

  const opsSignals: string[] = [];
  if (model.stockoutsPerWeek >= 5) opsSignals.push("High stockouts per week");
  if (model.serviceTicketsPerMonth >= 4) opsSignals.push("Frequent service tickets");
  if (model.pricingMarkupFeelsHigh) opsSignals.push("Pricing markup feels high");
  if (!model.commissionOrRevenueShareKnown) opsSignals.push("Economics (commission/rev-share) not clearly known");

  return { top, bottom, redFlagsOn, opsSignals };
}

function recommendationPack(pct: number, model: ScoreCardModel) {
  const ideas: string[] = [];

  // Always useful:
  ideas.push("Ask for a monthly performance report: downtime, stockouts, top sellers, refund rate, service response times.");
  ideas.push("Request a quarterly product review: rotate slow-movers, add employee-requested items, and adjust pricing bands.");

  // Score-based:
  if (pct < 70) {
    ideas.push("Get 2–3 competing bids and compare: pricing rules, stocking cadence, SLA, equipment refresh plan, and contract terms.");
    ideas.push("Negotiate an SLA: max response time, stockout thresholds, and refund timelines, with escalation contacts.");
  } else if (pct < 85) {
    ideas.push("Renegotiate the improvement plan: faster service windows, cleaner merchandising, and tighter stocking schedules.");
    ideas.push("Pilot a refreshed set of best-sellers + healthier options and measure sell-through for 30–60 days.");
  } else {
    ideas.push("Keep the provider, but lock in expectations with a simple scorecard review every quarter.");
    ideas.push("Ask for new ideas: seasonal rotations, local favorites, and upgraded payment options (tap/mobile).");
  }

  // Red flags:
  if (model.redFlags.contractAutoRenewTraps) ideas.push("Review renewal/termination clauses and add calendar reminders 90–120 days before renewal.");
  if (model.redFlags.repeatedPaymentFailures) ideas.push("Require payment system remediation + refund process audit.");
  if (model.redFlags.recurringExpiredProduct) ideas.push("Require dated inventory rotation checks and audit logs.");

  return ideas;
}

/* =============================================================================
   Component
============================================================================= */
export default function VendingScoreCard_v01_2024_01_18_chat(): JSX.Element {
  // Version: v1.0.0 (bottom-of-file version tag also required)

  const [model, setModel] = useState<ScoreCardModel>(() => buildDefaultModel());
  const [weights] = useState<WeightMap>(() => DEFAULT_WEIGHTS);

  const [toastMsg, setToastMsg] = useState<string>("");
  const toastTimer = useRef<number | null>(null);

  const [showExport, setShowExport] = useState(false);

  const computed = useMemo(() => computeWeightedScore(model, weights), [model, weights]);
  const grade = useMemo(() => scoreToGrade(computed.pct), [computed.pct]);
  const findings = useMemo(() => buildKeyFindings(model), [model]);
  const recs = useMemo(() => recommendationPack(computed.pct, model), [computed.pct, model]);

  const setField = <K extends keyof ScoreCardModel>(key: K, value: ScoreCardModel[K]) => {
    setModel((prev) => ({ ...prev, [key]: value }));
  };

  const setRating = (field: ScoreField, value: RatingValue) => {
    setModel((prev) => ({ ...prev, ratings: { ...prev.ratings, [field]: value } }));
  };

  const setRedFlag = (key: keyof ScoreCardModel["redFlags"], value: boolean) => {
    setModel((prev) => ({ ...prev, redFlags: { ...prev.redFlags, [key]: value } }));
  };

  const reset = () => {
    setModel(buildDefaultModel());
    notify("Reset to defaults.");
  };

  const notify = (msg: string) => {
    setToastMsg(msg);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToastMsg(""), 2400);
  };

  const reportText = useMemo(() => {
    const lines: string[] = [];
    lines.push("Vending Provider Scorecard");
    lines.push("=========================");
    lines.push(`Company: ${model.companyName || "(not set)"}`);
    lines.push(`Location/Dept: ${model.locationLabel || "(not set)"}`);
    lines.push(`Scope: ${model.scope}`);
    lines.push(`Provider: ${model.providerName || "(not set)"}`);
    if (model.contractEndDate) lines.push(`Contract End: ${model.contractEndDate}`);
    lines.push("");
    lines.push(`Score: ${computed.pct.toFixed(1)}% (Grade ${grade.grade})`);
    if (computed.redFlagCount > 0) {
      lines.push(`Red-Flag Penalty: -${computed.redFlagPenalty}% (${computed.redFlagCount} red flags)`);
    }
    lines.push("");

    lines.push("Top Strengths");
    findings.top.forEach((t) => lines.push(`- ${t.label}: ${t.value}/5 (${ratingLabel(t.value)})`));
    lines.push("");

    lines.push("Top Gaps");
    findings.bottom.forEach((b) => lines.push(`- ${b.label}: ${b.value}/5 (${ratingLabel(b.value)})`));
    lines.push("");

    if (findings.opsSignals.length) {
      lines.push("Operational Signals");
      findings.opsSignals.forEach((s) => lines.push(`- ${s}`));
      lines.push("");
    }

    lines.push("Recommendations");
    recs.slice(0, 8).forEach((r) => lines.push(`- ${r}`));
    lines.push("");

    lines.push("Notes");
    if (model.biggestWins) lines.push(`- Biggest wins: ${model.biggestWins}`);
    if (model.biggestPainPoints) lines.push(`- Biggest pain points: ${model.biggestPainPoints}`);
    if (model.wishList) lines.push(`- Wish list: ${model.wishList}`);

    return lines.join("\n");
  }, [model, computed, grade.grade, findings, recs]);

  const copyReport = async () => {
    try {
      await navigator.clipboard.writeText(reportText);
      notify("Copied report to clipboard.");
    } catch {
      notify("Copy failed (clipboard not available).");
    }
  };

  const downloadJSON = () => {
    const payload = {
      meta: {
        component: "VendingScoreCard.com",
        version: "v1.0.0",
        exportedAt: new Date().toISOString(),
      },
      model,
      computed,
      grade,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "vending-scorecard-export.json";
    a.click();
    URL.revokeObjectURL(url);
    notify("Downloaded JSON export.");
  };

  const scoreColorVariant =
    grade.intent === "success" ? "success" : grade.intent === "warning" ? "warning" : "danger";

  return (
    <Container fluid className="py-3">
      <Row className="g-3">
        {/* Left: Inputs */}
        <Col lg={7} xl={8}>
          <Stack gap={3}>
            <Card className="shadow-sm">
              <Card.Body>
                <Stack direction="horizontal" className="justify-content-between flex-wrap gap-2">
                  <div>
                    <h3 className="mb-1">Vending Provider Score Card</h3>
                    <div className="text-muted">
                      Evaluate your current vending / micro-market provider with a consistent, weighted rubric.
                    </div>
                  </div>
                  <Stack direction="horizontal" gap={2}>
                    <Button variant="outline-secondary" onClick={reset}>
                      <ArrowClockwise className="me-2" />
                      Reset
                    </Button>
                    <Button variant="primary" onClick={copyReport}>
                      <Clipboard className="me-2" />
                      Copy Report
                    </Button>
                    <Button variant="outline-primary" onClick={() => setShowExport(true)}>
                      <Download className="me-2" />
                      Export
                    </Button>
                  </Stack>
                </Stack>
                {toastMsg ? (
                  <Alert className="mt-3 mb-0" variant="info">
                    <InfoCircle className="me-2" />
                    {toastMsg}
                  </Alert>
                ) : null}
              </Card.Body>
            </Card>

            <Card className="shadow-sm">
              <Card.Header className="bg-white">
                <strong>Context</strong>
              </Card.Header>
              <Card.Body>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Label>Company</Form.Label>
                    <Form.Control
                      value={model.companyName}
                      onChange={(e) => setField("companyName", e.target.value)}
                      placeholder="e.g., Acme Manufacturing"
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Location / Department</Form.Label>
                    <Form.Control
                      value={model.locationLabel}
                      onChange={(e) => setField("locationLabel", e.target.value)}
                      placeholder="e.g., HQ - 2nd floor break room"
                    />
                  </Col>

                  <Col md={4}>
                    <Form.Label>Scope</Form.Label>
                    <Form.Select
                      value={model.scope}
                      onChange={(e) => setField("scope", e.target.value as ServiceType)}
                    >
                      <option value="Vending">Vending</option>
                      <option value="Micro-Market">Micro-Market</option>
                      <option value="Both">Both</option>
                    </Form.Select>
                  </Col>

                  <Col md={5}>
                    <Form.Label>Provider Name</Form.Label>
                    <Form.Control
                      value={model.providerName}
                      onChange={(e) => setField("providerName", e.target.value)}
                      placeholder="Current vendor name"
                    />
                  </Col>

                  <Col md={3}>
                    <Form.Label>Contract End</Form.Label>
                    <Form.Control
                      type="date"
                      value={model.contractEndDate}
                      onChange={(e) => setField("contractEndDate", e.target.value)}
                    />
                  </Col>

                  <Col md={4}>
                    <Form.Label># Locations Served</Form.Label>
                    <Form.Control
                      type="number"
                      value={model.locationsCount}
                      min={1}
                      onChange={(e) => setField("locationsCount", clampInt(Number(e.target.value || 1), 1, 999))}
                    />
                  </Col>

                  <Col md={4}>
                    <Form.Label># Vending Machines</Form.Label>
                    <Form.Control
                      type="number"
                      value={model.machinesCount}
                      min={0}
                      onChange={(e) => setField("machinesCount", clampInt(Number(e.target.value || 0), 0, 999))}
                    />
                  </Col>

                  <Col md={4}>
                    <Form.Label># Micro-Markets</Form.Label>
                    <Form.Control
                      type="number"
                      value={model.microMarketsCount}
                      min={0}
                      onChange={(e) => setField("microMarketsCount", clampInt(Number(e.target.value || 0), 0, 999))}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="shadow-sm">
              <Card.Header className="bg-white">
                <strong>Operational Inputs</strong>{" "}
                <Badge bg="light" text="dark" className="ms-2">
                  used for insights (not direct scoring)
                </Badge>
              </Card.Header>
              <Card.Body>
                <Row className="g-3">
                  <Col md={4}>
                    <Form.Label>Avg Weekly Foot Traffic</Form.Label>
                    <Form.Control
                      type="number"
                      value={model.avgWeeklyFootTraffic}
                      min={0}
                      onChange={(e) =>
                        setField("avgWeeklyFootTraffic", clampInt(Number(e.target.value || 0), 0, 500000))
                      }
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label>Stockouts / Week</Form.Label>
                    <Form.Control
                      type="number"
                      value={model.stockoutsPerWeek}
                      min={0}
                      onChange={(e) => setField("stockoutsPerWeek", clampInt(Number(e.target.value || 0), 0, 999))}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label>Service Tickets / Month</Form.Label>
                    <Form.Control
                      type="number"
                      value={model.serviceTicketsPerMonth}
                      min={0}
                      onChange={(e) =>
                        setField("serviceTicketsPerMonth", clampInt(Number(e.target.value || 0), 0, 999))
                      }
                    />
                  </Col>

                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      label="We clearly understand our commission / revenue-share economics"
                      checked={model.commissionOrRevenueShareKnown}
                      onChange={(e) => setField("commissionOrRevenueShareKnown", e.target.checked)}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      label="Pricing markup feels high vs expectations"
                      checked={model.pricingMarkupFeelsHigh}
                      onChange={(e) => setField("pricingMarkupFeelsHigh", e.target.checked)}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Ratings */}
            {CATEGORIES.map((cat) => (
              <Card key={cat.id} className="shadow-sm">
                <Card.Header className="bg-white">
                  <Stack direction="horizontal" className="justify-content-between flex-wrap gap-2">
                    <div>
                      <strong>{cat.title}</strong>
                      <div className="text-muted small">{cat.description}</div>
                    </div>
                    <Badge bg="light" text="dark">
                      1–5 rating
                    </Badge>
                  </Stack>
                </Card.Header>

                <Card.Body>
                  <Stack gap={3}>
                    {cat.items.map((item) => {
                      const val = model.ratings[item.field];
                      return (
                        <div key={item.field}>
                          <Stack direction="horizontal" className="justify-content-between gap-2">
                            <div>
                              <div className="fw-semibold">{item.label}</div>
                              <div className="text-muted small">{item.help}</div>
                            </div>
                            <Badge bg={val >= 4 ? "success" : val === 3 ? "secondary" : "warning"} className="ms-auto">
                              {val}/5 • {ratingLabel(val)}
                            </Badge>
                          </Stack>

                          <InputGroup className="mt-2">
                            <Form.Range
                              min={1}
                              max={5}
                              step={1}
                              value={val}
                              onChange={(e) => setRating(item.field, asRating(Number(e.target.value)))}
                            />
                            <InputGroup.Text style={{ minWidth: 64, justifyContent: "center" }}>{val}</InputGroup.Text>
                          </InputGroup>
                        </div>
                      );
                    })}
                  </Stack>
                </Card.Body>
              </Card>
            ))}

            <Card className="shadow-sm">
              <Card.Header className="bg-white">
                <strong>Red Flags</strong>{" "}
                <Badge bg="light" text="dark" className="ms-2">
                  apply a small penalty
                </Badge>
              </Card.Header>
              <Card.Body>
                <Row className="g-2">
                  <Col md={6}>
                    <Form.Check
                      type="checkbox"
                      label="Expired products showing up repeatedly"
                      checked={model.redFlags.recurringExpiredProduct}
                      onChange={(e) => setRedFlag("recurringExpiredProduct", e.target.checked)}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Check
                      type="checkbox"
                      label="Payment failures happen repeatedly"
                      checked={model.redFlags.repeatedPaymentFailures}
                      onChange={(e) => setRedFlag("repeatedPaymentFailures", e.target.checked)}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Check
                      type="checkbox"
                      label="Chronic stockouts (common items unavailable)"
                      checked={model.redFlags.chronicStockouts}
                      onChange={(e) => setRedFlag("chronicStockouts", e.target.checked)}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Check
                      type="checkbox"
                      label="Service is consistently slow"
                      checked={model.redFlags.slowService}
                      onChange={(e) => setRedFlag("slowService", e.target.checked)}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Check
                      type="checkbox"
                      label="Cleanliness is consistently poor"
                      checked={model.redFlags.poorCleanliness}
                      onChange={(e) => setRedFlag("poorCleanliness", e.target.checked)}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Check
                      type="checkbox"
                      label="Pricing gouging suspected"
                      checked={model.redFlags.priceGougingSuspected}
                      onChange={(e) => setRedFlag("priceGougingSuspected", e.target.checked)}
                    />
                  </Col>
                  <Col md={12}>
                    <Form.Check
                      type="checkbox"
                      label="Contract has auto-renew traps / hard termination terms"
                      checked={model.redFlags.contractAutoRenewTraps}
                      onChange={(e) => setRedFlag("contractAutoRenewTraps", e.target.checked)}
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="shadow-sm">
              <Card.Header className="bg-white">
                <strong>Notes</strong>
              </Card.Header>
              <Card.Body>
                <Row className="g-3">
                  <Col md={4}>
                    <Form.Label>Biggest Wins</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={model.biggestWins}
                      onChange={(e) => setField("biggestWins", e.target.value)}
                      placeholder="What’s working well?"
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label>Biggest Pain Points</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={model.biggestPainPoints}
                      onChange={(e) => setField("biggestPainPoints", e.target.value)}
                      placeholder="What’s frustrating?"
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label>Wish List</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={model.wishList}
                      onChange={(e) => setField("wishList", e.target.value)}
                      placeholder="What would make this ideal?"
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Stack>
        </Col>

        {/* Right: Score Summary */}
        <Col lg={5} xl={4}>
          <div style={{ position: "sticky", top: 12 }}>
            <Card className="shadow-sm">
              <Card.Body>
                <Stack gap={3}>
                  <Stack direction="horizontal" className="justify-content-between">
                    <div>
                      <div className="text-muted">Overall Score</div>
                      <div className="d-flex align-items-center gap-2">
                        <div style={{ fontSize: 34, fontWeight: 800, lineHeight: 1 }}>
                          {computed.pct.toFixed(1)}%
                        </div>
                        <Badge bg={scoreColorVariant} style={{ fontSize: 16 }}>
                          Grade {grade.grade}
                        </Badge>
                      </div>
                    </div>

                    <div className="text-end">
                      <div className="text-muted">Red Flags</div>
                      <div className="d-flex align-items-center justify-content-end gap-2">
                        {computed.redFlagCount > 0 ? (
                          <>
                            <ExclamationTriangleFill />
                            <strong>{computed.redFlagCount}</strong>
                          </>
                        ) : (
                          <>
                            <CheckCircleFill />
                            <strong>0</strong>
                          </>
                        )}
                      </div>
                      {computed.redFlagCount > 0 ? (
                        <div className="text-muted small">Penalty: -{computed.redFlagPenalty}%</div>
                      ) : (
                        <div className="text-muted small">No penalty applied</div>
                      )}
                    </div>
                  </Stack>

                  <div>
                    <ProgressBar
                      now={computed.pct}
                      label={`${computed.pct.toFixed(0)}%`}
                      variant={scoreColorVariant}
                      style={{ height: 18 }}
                    />
                    <div className="text-muted small mt-2">
                      Weighted rubric (1–5). Red flags apply a small realism penalty.
                    </div>
                  </div>

                  <Card className="bg-light border-0">
                    <Card.Body>
                      <Stack direction="horizontal" className="justify-content-between">
                        <div className="text-muted">Snapshot</div>
                        <Badge bg="dark">
                          {model.scope === "Both" ? "Vending + Market" : model.scope}
                        </Badge>
                      </Stack>

                      <ListGroup variant="flush" className="mt-2">
                        <ListGroup.Item className="bg-transparent px-0 d-flex justify-content-between">
                          <span className="text-muted">Provider</span>
                          <span className="fw-semibold">{model.providerName || "—"}</span>
                        </ListGroup.Item>
                        <ListGroup.Item className="bg-transparent px-0 d-flex justify-content-between">
                          <span className="text-muted">Locations</span>
                          <span className="fw-semibold">{model.locationsCount}</span>
                        </ListGroup.Item>
                        <ListGroup.Item className="bg-transparent px-0 d-flex justify-content-between">
                          <span className="text-muted">Machines</span>
                          <span className="fw-semibold">{model.machinesCount}</span>
                        </ListGroup.Item>
                        <ListGroup.Item className="bg-transparent px-0 d-flex justify-content-between">
                          <span className="text-muted">Micro-Markets</span>
                          <span className="fw-semibold">{model.microMarketsCount}</span>
                        </ListGroup.Item>
                      </ListGroup>
                    </Card.Body>
                  </Card>

                  <div>
                    <div className="fw-semibold mb-2">
                      Strengths <StarFill className="ms-1" />
                    </div>
                    <Stack gap={2}>
                      {findings.top.map((t) => (
                        <div key={t.field} className="d-flex align-items-center justify-content-between">
                          <span className="text-muted">{t.label}</span>
                          <Badge bg={t.value >= 4 ? "success" : "secondary"}>
                            {t.value}/5
                          </Badge>
                        </div>
                      ))}
                    </Stack>
                  </div>

                  <div>
                    <div className="fw-semibold mb-2">Gaps to Fix</div>
                    <Stack gap={2}>
                      {findings.bottom.map((b) => (
                        <div key={b.field} className="d-flex align-items-center justify-content-between">
                          <span className="text-muted">{b.label}</span>
                          <Badge bg={b.value <= 2 ? "warning" : "secondary"}>
                            {b.value}/5
                          </Badge>
                        </div>
                      ))}
                    </Stack>
                  </div>

                  {findings.opsSignals.length ? (
                    <Alert variant="warning" className="mb-0">
                      <div className="fw-semibold mb-1">Signals</div>
                      <ul className="mb-0">
                        {findings.opsSignals.map((s) => (
                          <li key={s}>{s}</li>
                        ))}
                      </ul>
                    </Alert>
                  ) : (
                    <Alert variant="success" className="mb-0">
                      <div className="fw-semibold">Signals look healthy</div>
                      <div className="small">No major operational indicators triggered.</div>
                    </Alert>
                  )}

                  <div>
                    <div className="fw-semibold mb-2">Recommended Next Steps</div>
                    <ListGroup>
                      {recs.slice(0, 6).map((r) => (
                        <ListGroup.Item key={r}>{r}</ListGroup.Item>
                      ))}
                    </ListGroup>
                  </div>

                  <Stack direction="horizontal" gap={2} className="justify-content-between flex-wrap">
                    <Button variant="primary" onClick={copyReport}>
                      <ClipboardCheck className="me-2" />
                      Copy Full Report
                    </Button>
                    <Button variant="outline-primary" onClick={() => setShowExport(true)}>
                      <Download className="me-2" />
                      Export
                    </Button>
                  </Stack>
                </Stack>
              </Card.Body>
            </Card>
          </div>
        </Col>
      </Row>

      {/* Export Modal */}
      <Modal show={showExport} onHide={() => setShowExport(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Export / Share</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <InfoCircle className="me-2" />
            Use the report below in an email to your provider, leadership, or to compare competing bids.
          </Alert>

          <Stack direction="horizontal" gap={2} className="flex-wrap mb-2">
            <Button variant="primary" onClick={copyReport}>
              <Clipboard className="me-2" />
              Copy Report
            </Button>
            <Button variant="outline-primary" onClick={downloadJSON}>
              <Download className="me-2" />
              Download JSON
            </Button>
          </Stack>

          <Form.Control as="textarea" rows={16} value={reportText} readOnly />
          <div className="text-muted small mt-2">
            Tip: Add vendor SLA targets (response times, stockout thresholds) and request monthly KPI reporting.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowExport(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

/* ============================================================================
   Version: v1.0.0
   File: VendingScoreCard.tsx
   Description: ReactSet scorecard component to evaluate a company’s current
                vending/micro-market services provider with weighted scoring,
                red-flag penalties, and exportable report text/JSON.
   ============================================================================ */
